// Fixed-capacity object pool with a dense active prefix (plan §4.5 / §9-C).
// All objects are created once up front, so acquire/release never allocate and the
// GC never runs during gameplay. Active items occupy indices [0, count); release
// uses swap-remove to keep them dense, so iteration skips nothing.
//
// Iteration + release pattern (release invalidates index i by swapping the last item
// into it, so re-test i):
//   for (let i = 0; i < pool.count; i++) {
//     const item = pool.items[i];
//     if (shouldRemove(item)) { pool.releaseAt(i); i--; continue; }
//   }

export class Pool<T> {
  readonly items: readonly T[];
  /** Number of active items, occupying items[0..count). */
  count = 0;

  constructor(capacity: number, factory: () => T) {
    const arr = new Array<T>(capacity);
    for (let i = 0; i < capacity; i++) arr[i] = factory();
    this.items = arr;
  }

  get capacity(): number {
    return this.items.length;
  }

  /** Take a reusable object (caller must fully (re)initialize it). null if exhausted. */
  acquire(): T | null {
    if (this.count >= this.items.length) return null; // drop on exhaustion (plan §9-C)
    return this.items[this.count++];
  }

  /** Release the active item at dense index `i` via swap-remove. */
  releaseAt(i: number): void {
    const last = --this.count;
    if (i !== last) {
      const arr = this.items as T[];
      const tmp = arr[i];
      arr[i] = arr[last];
      arr[last] = tmp;
    }
  }

  clear(): void {
    this.count = 0;
  }
}
